import { LayoutAlignItems, LayoutDirection, LayoutJustifyContent } from '@/constants/layoutTypes';
import { AnnualizedMileage } from '@/schemas/sceneDataSchema';
import {
  Card,
  Checkbox,
  Divider,
  FormHelperText,
  Icon,
  Link,
  Radio,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { StyledChip } from '../selects/BaseChipElement';
import { UseFormReturn } from 'react-hook-form';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { GenericButton } from '@/components/DynamicUI/formElements/buttons/GenericButton';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import NumberFlow from '@number-flow/react';
import useModal from '@/hooks/use-modals';
import { ModalDialog } from '@/components/DynamicUI/ModalDialog';
import { Layout } from '@/components/DynamicUI/Layout';
import { Carousel } from '@/components/DynamicUI/Carousel';
import { CarouselTile } from '@/components/DynamicUI/CarouselTile';
import { TypographyColors, TypographyVariants } from '@/constants/typographyTypes';
import { Image } from '@/components/DynamicUI/Image';

enum USAGE_TYPES {
  BUSINESS = 'business',
  COMMUTE = 'commute',
  OCCASIONAL = 'occasional',
  RIDESHARE = 'rideshare',
}

const PRIMARY_USAGE_PRIORITY = {
  [USAGE_TYPES.BUSINESS]: 4,
  [USAGE_TYPES.RIDESHARE]: 3,
  [USAGE_TYPES.COMMUTE]: 2,
  [USAGE_TYPES.OCCASIONAL]: 1,
} as const;

enum RIDESHARE_TIMES {
  PART_TIME = 'part-time',
  FULL_TIME = 'full-time',
}

const DEFAULT_COMMUTE_VALUES = {
  mileage: 12,
  daysPerWeek: 5,
};

function initialUsage(primaryUsage: string | null) {
  const state: Record<string, boolean> = {
    [USAGE_TYPES.COMMUTE]: false,
    [USAGE_TYPES.OCCASIONAL]: false,
    [USAGE_TYPES.RIDESHARE]: false,
    [USAGE_TYPES.BUSINESS]: false,
  };

  if (primaryUsage) {
    state[primaryUsage] = true;
  }

  return state;
}

export const AnnualizedMileageElement = ({
  form,
  component,
}: {
  form: UseFormReturn;
  component: AnnualizedMileage;
}) => {
  const { getModal } = useModal();
  const [customMileage, setCustomMileage] = useState<number | null>(component.annualized_mileage.value);
  const [modalCustomMileage, setModalCustomMileage] = useState<number | null>();

  const customMileageModal = useMemo(() => getModal('edit_mileage'), [getModal]);
  const businessUseExplainerModal = useMemo(() => getModal('business_use_explainer'), [getModal]);

  const [usageStates, setUsageStates] = useState(initialUsage(component.primary_usage.value));

  const [commuteState, setCommuteState] = useState(DEFAULT_COMMUTE_VALUES);

  const [rideshareTime, setRideshareTime] = useState<RIDESHARE_TIMES | null>(RIDESHARE_TIMES.PART_TIME);

  const safeParseInt = (input: unknown, defaultValue: number): number => {
    const parsed = parseInt(input as string);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const handleUsageChange = useCallback(
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      // reset commute state to default when commute usage is unchecked
      const isInvalidcommuteValue = form.getFieldState(
        encodeFieldKey(component.commute_distance),
        form.formState
      ).error;
      if ((key as USAGE_TYPES) === USAGE_TYPES.COMMUTE && !checked && isInvalidcommuteValue) {
        setCommuteState(DEFAULT_COMMUTE_VALUES);
        form.setValue(encodeFieldKey(component.commute_distance), 12, { shouldValidate: true });
      }

      setUsageStates((prev) => ({
        ...prev,
        [key]: checked,
      }));
    },
    [component.commute_distance, form]
  );

  const handleCommuteMileageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = safeParseInt(event.target.value, 0);
      setCommuteState((prev) => ({
        ...prev,
        mileage: value,
      }));
      form.setValue(encodeFieldKey(component.commute_distance), value, { shouldValidate: true });
    },
    [component.commute_distance, form]
  );

  const handleCommuteDaysChange = useCallback(
    (days: number) => () => {
      setCommuteState((prev) => ({
        ...prev,
        daysPerWeek: days,
      }));
    },
    [setCommuteState]
  );

  const handleRideshareTimeClick = useCallback(
    (time: RIDESHARE_TIMES) => () => {
      setRideshareTime(time);
    },
    [setRideshareTime]
  );

  const handleCustomMileageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = safeParseInt(event.target.value, 0);
      setModalCustomMileage(value);
      form.setValue(encodeFieldKey(component.annualized_mileage), value, { shouldValidate: true });
    },
    [form, component.annualized_mileage]
  );

  const values = useMemo(
    () => ({
      annualizedMileage: component.annualized_mileage.value,
      primaryUsage: component.primary_usage.value,
      businessMileage: component.business_mileage,
      occasionalMileage: component.occasional_mileage,
      rideshareMileageFullTime: component.rideshare_mileage_full_time,
      rideshareMileagePartTime: component.rideshare_mileage_part_time,
      commuteDistance: component.commute_distance.value,
    }),
    [
      component.annualized_mileage.value,
      component.primary_usage.value,
      component.business_mileage,
      component.occasional_mileage,
      component.rideshare_mileage_full_time,
      component.rideshare_mileage_part_time,
      component.commute_distance.value,
    ]
  );

  const handleBusinessUseExplainerLinkClick = useCallback(() => {
    businessUseExplainerModal?.open();
  }, [businessUseExplainerModal]);

  const usages = useMemo(
    () => [
      {
        key: USAGE_TYPES.COMMUTE,
        label: 'To commute',
        description: 'This includes driving your children to school.',
        value: (component.commuting_weeks ?? 52) * 2 * (commuteState.daysPerWeek || 0) * (commuteState.mileage || 0),
      },
      {
        key: USAGE_TYPES.OCCASIONAL,
        label: 'Occasional/pleasure driving',
        description: 'This includes occasional errands, dinner with friends, or road trips.',
        value: values.occasionalMileage,
      },
      {
        key: USAGE_TYPES.RIDESHARE,
        label: 'Rideshare or delivery',
        description: 'App based ride for hire services like DoorDash, Instacart, or Uber.',
        value:
          rideshareTime === RIDESHARE_TIMES.PART_TIME
            ? values.rideshareMileagePartTime
            : values.rideshareMileageFullTime,
      },
      {
        key: USAGE_TYPES.BUSINESS,
        label: 'Business use',
        description: 'This includes driving for work, but not commuting.',
        value: values.businessMileage,
      },
    ],
    [
      component.commuting_weeks,
      commuteState.daysPerWeek,
      commuteState.mileage,
      rideshareTime,
      values.businessMileage,
      values.occasionalMileage,
      values.rideshareMileageFullTime,
      values.rideshareMileagePartTime,
    ]
  );

  const calculatedAnnualMileageEstimate = useMemo(() => {
    return usages.reduce((acc, usage) => {
      if (usageStates[usage.key]) {
        return Math.min(acc + usage.value, 99999);
      }

      return acc;
    }, 0);
  }, [usageStates, usages]);

  const hasInteractedWithForm = !!calculatedAnnualMileageEstimate;

  const handleEditMileageButtonClick = useCallback(() => {
    customMileageModal?.open();
  }, [customMileageModal]);

  const handleResetMileageButtonClick = () => {
    setCustomMileage(null);
  };

  const editMileageButton = useMemo(() => {
    if (!hasInteractedWithForm) return null;

    if (!customMileage) {
      return (
        <GenericButton
          id='edit_mileage_button'
          startIcon={<Icon color='primary'>edit_note</Icon>}
          color='primary'
          label='Edit'
          size='small'
          variant='outlined'
          fullWidth={false}
          onClick={handleEditMileageButtonClick}
        />
      );
    } else {
      return (
        <GenericButton
          id='reset_mileage_button'
          startIcon={<Icon color='primary'>restart_alt</Icon>}
          color='primary'
          label='Reset'
          size='small'
          variant='outlined'
          fullWidth={false}
          onClick={handleResetMileageButtonClick}
        />
      );
    }
  }, [customMileage, handleEditMileageButtonClick, hasInteractedWithForm]);

  const handleCustomMileageUpdateButtonClick = useCallback(() => {
    if (!modalCustomMileage) return;
    setCustomMileage(modalCustomMileage);
    customMileageModal?.close();
  }, [customMileageModal, modalCustomMileage]);

  const handleCustomMileageCancelButtonClick = useCallback(() => {
    customMileageModal?.close();
  }, [customMileageModal]);

  const handleBusinessUseExplainerGotItButtonClick = useCallback(() => {
    businessUseExplainerModal?.close();
  }, [businessUseExplainerModal]);

  useEffect(() => {
    form.setValue(encodeFieldKey(component.annualized_mileage), customMileage ?? calculatedAnnualMileageEstimate, {
      shouldValidate: hasInteractedWithForm,
    });

    const primaryUsage = usages.reduce(
      (acc, usage) => {
        if (usageStates[usage.key]) {
          if (!acc) {
            return usage.key;
          }

          if (PRIMARY_USAGE_PRIORITY[usage.key] > PRIMARY_USAGE_PRIORITY[acc as USAGE_TYPES]) {
            return usage.key;
          }
        }

        return acc;
      },
      undefined as string | undefined
    );

    form.setValue(encodeFieldKey(component.primary_usage), primaryUsage);
  }, [
    calculatedAnnualMileageEstimate,
    component.annualized_mileage,
    component.primary_usage,
    customMileage,
    form,
    hasInteractedWithForm,
    usageStates,
    usages,
  ]);

  useEffect(() => {
    form.setValue(encodeFieldKey(component.primary_usage), component.primary_usage.value);
    form.setValue(encodeFieldKey(component.annualized_mileage), component.annualized_mileage.value);
    form.setValue(encodeFieldKey(component.commute_distance), component.commute_distance.value ?? 12);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <ColumnLayout spacing={5}>
        <ColumnLayout spacing={4}>
          <ColumnLayout spacing={0}>
            <Typography variant='h3'>What do you use your vehicle for?</Typography>

            <Typography
              variant='body1'
              color={TypographyColors.TEXT_SECONDARY}
            >
              Select all that apply.
            </Typography>
          </ColumnLayout>

          <DividedList>
            {usages.map((usage) => (
              <div key={usage.key}>
                <Label>
                  <ColumnLayout spacing={0}>
                    <Typography
                      variant='body1'
                      color='textPrimary'
                    >
                      {usage.label}
                    </Typography>
                    <Typography
                      variant='body2'
                      color={TypographyColors.TEXT_SECONDARY}
                    >
                      {usage.description}
                    </Typography>
                  </ColumnLayout>

                  <Checkbox
                    onChange={handleUsageChange(usage.key)}
                    checked={usageStates[usage.key]}
                  />
                </Label>

                {usage.key === USAGE_TYPES.BUSINESS && (
                  <Link
                    onClick={handleBusinessUseExplainerLinkClick}
                    color={TypographyColors.TEXT_SECONDARY}
                  >
                    <Typography
                      variant={TypographyVariants.BODY2}
                      color={TypographyColors.TEXT_SECONDARY}
                    >
                      View examples
                    </Typography>
                  </Link>
                )}
              </div>
            ))}
          </DividedList>
        </ColumnLayout>

        {usageStates[USAGE_TYPES.COMMUTE] && (
          <ColumnLayout spacing={3}>
            <ColumnLayout spacing={3}>
              <Typography variant='h3'>How many days per week do you typically commute?</Typography>

              <RowLayout spacing={1}>
                {Array.from({ length: 7 }, (_, index) => {
                  const selected = index + 1 === commuteState.daysPerWeek;

                  return (
                    <StyledChip
                      key={index}
                      icon={selected ? <Icon color='primary'>check</Icon> : undefined}
                      label={`${index + 1}`}
                      onClick={handleCommuteDaysChange(index + 1)}
                      size={'small'}
                      variant={'outlined'}
                      selected={selected}
                    />
                  );
                })}
              </RowLayout>
            </ColumnLayout>

            <ColumnLayout spacing={3}>
              <Typography variant='h3'>What’s your one-way commute distance?</Typography>
              <TextField
                variant='outlined'
                label='Mileage'
                placeholder='Enter distance'
                error={!!form.getFieldState(encodeFieldKey(component.commute_distance)).error}
                helperText={form.getFieldState(encodeFieldKey(component.commute_distance)).error?.message}
                fullWidth
                value={commuteState.mileage || ''}
                onChange={handleCommuteMileageChange}
                slotProps={{
                  input: {
                    endAdornment: <span>miles</span>,
                  },
                }}
              />
            </ColumnLayout>
          </ColumnLayout>
        )}

        {usageStates[USAGE_TYPES.RIDESHARE] && (
          <ColumnLayout spacing={3}>
            <ColumnLayout spacing={1}>
              <Typography variant='h3'>Do you drive rideshare part-time or full-time?</Typography>
              <Typography
                variant='body1'
                color={TypographyColors.TEXT_SECONDARY}
              >
                We’ll make sure your policy includes rideshare coverage to ensure you’re protected.
              </Typography>
            </ColumnLayout>

            <DividedList>
              <Label>
                <Typography variant='body1'>Part-time</Typography>

                <Radio
                  name='rideshare-time'
                  value={RIDESHARE_TIMES.PART_TIME}
                  checked={rideshareTime == RIDESHARE_TIMES.PART_TIME}
                  aria-label='Part-time'
                  onClick={handleRideshareTimeClick(RIDESHARE_TIMES.PART_TIME)}
                />
              </Label>

              <Label>
                <Typography variant='body1'>Full-time</Typography>

                <Radio
                  name='rideshare-time'
                  value={RIDESHARE_TIMES.FULL_TIME}
                  checked={rideshareTime == RIDESHARE_TIMES.FULL_TIME}
                  aria-label='Full-time'
                  onClick={handleRideshareTimeClick(RIDESHARE_TIMES.FULL_TIME)}
                />
              </Label>
            </DividedList>
          </ColumnLayout>
        )}

        <MileageCard variant='outlined'>
          <RowLayout
            spacing={2}
            justifyContent={LayoutJustifyContent.SPACE_BETWEEN}
          >
            <ColumnLayout spacing={0}>
              <Typography
                variant='body2'
                color={TypographyColors.TEXT_SECONDARY}
              >
                Annualized Mileage
              </Typography>

              <NumberContainer annualMileage={customMileage ?? calculatedAnnualMileageEstimate}>
                <Typography
                  variant='h2'
                  color='textPrimary'
                  className='number'
                >
                  <NumberFlow value={customMileage ?? calculatedAnnualMileageEstimate} />
                </Typography>

                <Typography
                  variant='h2'
                  color={TypographyColors.TEXT_SECONDARY}
                  className='placeholder'
                >
                  --,---
                </Typography>
                {form.getFieldState(encodeFieldKey(component.annualized_mileage), form.formState).error && (
                  <FormHelperText error={true}>
                    {form.getFieldState(encodeFieldKey(component.annualized_mileage), form.formState).error?.message}
                  </FormHelperText>
                )}
              </NumberContainer>
            </ColumnLayout>

            {editMileageButton}
          </RowLayout>
        </MileageCard>
      </ColumnLayout>
      <ModalDialog
        id='edit_mileage'
        status='closed'
        footerChildren={
          <>
            <GenericButton
              id='update_custom_mileage_button'
              label='Update'
              variant='contained'
              onClick={handleCustomMileageUpdateButtonClick}
              fullWidth
            />
            <GenericButton
              id='cancel_custom_mileage_button'
              label='Cancel'
              variant='outlined'
              onClick={handleCustomMileageCancelButtonClick}
              fullWidth
            />
          </>
        }
      >
        <ColumnLayout spacing={2}>
          <ColumnLayout spacing={2}>
            <Typography variant='h2'>Estimate annualized mileage</Typography>

            <Typography
              variant='body1'
              color={TypographyColors.TEXT_SECONDARY}
            >
              We’ve estimated your annual mileage based on your responses. Feel free to provide your own estimate below.
            </Typography>
          </ColumnLayout>

          <TextField
            variant='outlined'
            label='Mileage'
            error={!!form.getFieldState(encodeFieldKey(component.annualized_mileage)).error}
            helperText={form.getFieldState(encodeFieldKey(component.annualized_mileage)).error?.message}
            value={modalCustomMileage ?? ''}
            onChange={handleCustomMileageChange}
          />
        </ColumnLayout>
      </ModalDialog>
      <ModalDialog
        id='business_use_explainer'
        status='closed'
        footerChildren={
          <>
            <GenericButton
              id='close_business_use_explainer_button'
              label='Ok, got it'
              variant='contained'
              onClick={handleBusinessUseExplainerGotItButtonClick}
              fullWidth
            />
          </>
        }
      >
        <ColumnLayout>
          <Typography variant='h2'>What is business use?</Typography>
          <ColumnLayout spacing={4}>
            <Typography
              variant='body1'
              color={TypographyColors.TEXT_SECONDARY}
            >
              Regular use of your vehicle for official business purposes beyond normal commuting.
            </Typography>
            <Carousel>
              <CarouselTile>
                <Stack
                  height='100%'
                  justifyContent={LayoutJustifyContent.SPACE_BETWEEN}
                  alignItems={LayoutAlignItems.STRETCH}
                >
                  <ColumnLayout spacing={2}>
                    <Image
                      id={'carousel-real-estate'}
                      alt={null}
                      width={null}
                      backgroundColor={null}
                    ></Image>
                    <Typography variant='h4'>The realtor</Typography>
                    <Typography
                      variant='body1'
                      color={TypographyColors.TEXT_SECONDARY}
                    >
                      Tina regularly uses their vehicle to travel to and from listings to meet clients.
                    </Typography>
                  </ColumnLayout>
                  <ColumnLayout spacing={4}>
                    <Divider />
                    <RowLayout justifyContent={LayoutJustifyContent.SPACE_BETWEEN}>
                      <Typography
                        variant='body1'
                        fontWeight='medium'
                      >
                        Business use?
                      </Typography>
                      <Typography variant='body1'>Yes</Typography>
                    </RowLayout>
                  </ColumnLayout>
                </Stack>
              </CarouselTile>
              <CarouselTile>
                <Stack sx={{ height: '100%' }}>
                  <Stack
                    spacing={2}
                    sx={{ marginBottom: 'auto' }}
                  >
                    <Image
                      id={'carousel-sales-rep'}
                      alt={null}
                      width={null}
                      backgroundColor={null}
                    ></Image>
                    <Typography variant='h4'>The sales rep</Typography>
                    <Typography
                      variant='body1'
                      color={TypographyColors.TEXT_SECONDARY}
                    >
                      Maria frequently uses their vehicle to drive to small businesses to offer consultations.
                    </Typography>
                  </Stack>
                  <ColumnLayout spacing={4}>
                    <Divider />
                    <Layout
                      id={null}
                      spacing={0}
                      direction={LayoutDirection.ROW}
                      align_items={LayoutAlignItems.START}
                      justify_content={LayoutJustifyContent.SPACE_BETWEEN}
                    >
                      <Typography
                        variant='body1'
                        fontWeight='medium'
                      >
                        Business use?
                      </Typography>
                      <Typography variant='body1'>Yes</Typography>
                    </Layout>
                  </ColumnLayout>
                </Stack>
              </CarouselTile>
              <CarouselTile>
                <ColumnLayout spacing={2}>
                  <Image
                    id={'carousel-caret-car'}
                    alt={null}
                    width={null}
                    backgroundColor={null}
                  ></Image>
                  <Typography variant='h4'>Still need help?</Typography>
                  <Typography
                    variant='body1'
                    color={TypographyColors.TEXT_SECONDARY}
                  >
                    If the below applies to you then you should select business use.
                  </Typography>
                  <RowLayout spacing={2}>
                    <Icon color='success'>check_circle</Icon>
                    <Typography
                      variant='body1'
                      color={TypographyColors.TEXT_SECONDARY}
                    >
                      Your car is NOT owned by the business.
                    </Typography>
                  </RowLayout>
                  <RowLayout spacing={2}>
                    <Icon color='success'>check_circle</Icon>
                    <Typography
                      variant='body1'
                      color={TypographyColors.TEXT_SECONDARY}
                    >
                      Your car is used for work-related tasks, not just commuting.
                    </Typography>
                  </RowLayout>
                </ColumnLayout>
              </CarouselTile>
            </Carousel>
          </ColumnLayout>
        </ColumnLayout>
      </ModalDialog>
    </>
  );
};

const MileageCard = styled(Card)`
  padding: 20px;
  border-radius: var(--border-radius-medium);
  box-shadow: var(--shadow-large);
  background-color: var(--mui-palette-background-main);
`;

const ColumnLayout = ({
  spacing = 0,
  alignItems = LayoutAlignItems.STRETCH,
  justifyContent = LayoutJustifyContent.START,
  children,
}: {
  spacing?: number;
  alignItems?: LayoutAlignItems;
  justifyContent?: LayoutJustifyContent;
  children: React.ReactNode;
  height?: string;
}) => (
  <Layout
    id={null}
    spacing={spacing}
    direction={LayoutDirection.COLUMN}
    align_items={alignItems}
    justify_content={justifyContent}
  >
    {children}
  </Layout>
);

const RowLayout = ({
  spacing = 0,
  alignItems = LayoutAlignItems.CENTER,
  justifyContent = LayoutJustifyContent.START,
  children,
}: {
  spacing?: number;
  alignItems?: LayoutAlignItems;
  justifyContent?: LayoutJustifyContent;
  children: React.ReactNode;
}) => (
  <Layout
    id={null}
    spacing={spacing}
    direction={LayoutDirection.ROW}
    align_items={alignItems}
    justify_content={justifyContent}
  >
    {children}
  </Layout>
);

const DividedList = styled('div')`
  > * {
    padding: 12px 4px 12px 12px;

    &:not(:first-child) /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */ {
      border-top: 1px solid var(--mui-palette-border-main);
    }
  }
`;

const Label = styled('label')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: var(--mui-spacing);
`;

const NumberContainer = styled('div')<{ annualMileage: number }>`
  position: relative;

  & > .number {
    opacity: ${({ annualMileage }) => (annualMileage ? 1 : 0)};
  }
  & > .placeholder {
    opacity: ${({ annualMileage }) => (annualMileage ? 0 : 1)};
    position: absolute;
    top: 0;
    left: 0;
  }
`;
